import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { r8, s4 } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  PEEK_BOTTOM_OVERLAP,
  PEEK_IN_DURATION_MS,
  PEEK_OUT_DURATION_MS,
  PeekNotificationBanner,
} from '@ardoq/status-ui';
import type { AssistantBanner } from '../assistantTypes';

type StackedBannersProps = {
  banners: AssistantBanner[];
  onDismiss?: (banner: AssistantBanner) => void;
  i18nLocale: I18nLocale;
};

type DisplayedBanner = {
  banner: AssistantBanner;
  isVisible: boolean;
};

const hasSameBanners = (
  previousBanners: AssistantBanner[],
  banners: AssistantBanner[]
): boolean =>
  previousBanners.length === banners.length &&
  previousBanners.every((banner, index) => banner.id === banners[index]?.id);

const toDisplayedBanners = (
  displayed: DisplayedBanner[],
  banners: AssistantBanner[]
): DisplayedBanner[] => {
  const currentIds = new Set(banners.map(({ id }) => id));
  const displayedIds = new Set(displayed.map(({ banner }) => banner.id));

  return [
    ...displayed.map(({ banner }) => ({
      banner,
      isVisible: currentIds.has(banner.id),
    })),
    ...banners
      .filter(({ id }) => !displayedIds.has(id))
      .map(banner => ({ banner, isVisible: true })),
  ];
};

type StackedBannerSlotProps = {
  banner: AssistantBanner;
  isVisible: boolean;
  onDismiss?: (banner: AssistantBanner) => void;
  onExited: (bannerId: string) => void;
  i18nLocale: I18nLocale;
};

const StackedBannerSlot = ({
  banner,
  isVisible,
  onDismiss,
  onExited,
  i18nLocale,
}: StackedBannerSlotProps) => (
  <Slot $isExiting={!isVisible}>
    <StackedBanner
      isVisible={isVisible}
      status={banner.status}
      onDismiss={onDismiss ? () => onDismiss(banner) : undefined}
      dismissLabel={t(
        i18nLocale,
        'assistant:actions.dismissBanner',
        { message: banner.message },
        'Dismiss message: {{message}}'
      )}
      onPeekOutComplete={() => onExited(banner.id)}
    >
      {banner.message}
    </StackedBanner>
  </Slot>
);

export const StackedBanners = ({
  banners,
  onDismiss,
  i18nLocale,
}: StackedBannersProps) => {
  const [displayedBanners, setDisplayedBanners] = useState<DisplayedBanner[]>(
    () => banners.map(banner => ({ banner, isVisible: true }))
  );
  const [previousBanners, setPreviousBanners] = useState(banners);

  // derive during render so a removed banner is never unmounted before it animates out
  if (!hasSameBanners(previousBanners, banners)) {
    setPreviousBanners(banners);
    setDisplayedBanners(current => toDisplayedBanners(current, banners));
  }

  const removeBanner = (bannerId: string) =>
    setDisplayedBanners(current =>
      // a banner that came back while it was animating out is visible again, not exited
      current.some(
        ({ banner, isVisible }) => banner.id === bannerId && isVisible
      )
        ? current
        : current.filter(({ banner }) => banner.id !== bannerId)
    );

  if (displayedBanners.length === 0) return null;

  return (
    <Stack>
      {displayedBanners.map(({ banner, isVisible }) => (
        <StackedBannerSlot
          key={banner.id}
          banner={banner}
          isVisible={isVisible}
          onDismiss={onDismiss}
          onExited={removeBanner}
          i18nLocale={i18nLocale}
        />
      ))}
    </Stack>
  );
};

const Stack = styled.div`
  position: absolute;
  bottom: calc(100% - ${PEEK_BOTTOM_OVERLAP});
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: ${PEEK_BOTTOM_OVERLAP};
`;

const expand = keyframes`
  from {
    grid-template-rows: 0fr;
  }
  to {
    grid-template-rows: 1fr;
  }
`;

const Slot = styled.div<{ $isExiting: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExiting }) => ($isExiting ? '0fr' : '1fr')};
  transition: grid-template-rows ${PEEK_OUT_DURATION_MS}ms ease-in;
  animation: ${expand} ${PEEK_IN_DURATION_MS}ms ease-out;
  padding-bottom: ${s4};

  > * {
    min-height: 0;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const StackedBanner = styled(PeekNotificationBanner)`
  && {
    position: static;
    padding-bottom: ${s4};
    border-radius: ${r8};
  }
`;
